import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import { Dialog } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  WizardStepper,
  WizardStepContent,
  EntitySelector,
  PlanDetailsStep,
  DemographicsConfigStep,
  StrategySettingsStep,
  StrategyReviewStep,
  StrategyList,
  StrategyDetail,
} from "@/features/content/components";
import { contentService } from "@/features/content/services/content.service";
import { agentService } from "@/features/agent/services/agent.service";
import { brandingService } from "@/features/branding/services/branding.service";
import { campaignService } from "@/features/campaign/services/campaign.service";
import { integrationService } from "@/features/integration/services/integration.service";
import { CreateAgentsForm } from "@/features/agent/components";
import { BrandForm, BrandDetail } from "@/features/branding/components";
import { CampaignForm, CampaignDetail } from "@/features/campaign/components";
import type {
  CreateContentStrategyParams,
  UpdateContentStrategyParams,
  ContentStrategyConfig,
  ContentStrategyConfigDemographics,
  ContentStrategyStatus,
  ContentStrategyMode,
  ContentStrategyWithDetails,
  ContentItem,
} from "@/features/content/interfaces";
import type { 
  Agent, 
  AgentStatus, 
  CompanyAgentStatus,
  CreateAgentsParams 
} from "@/features/agent/interfaces";
import type {
  CreateBrandParams,
  UpdateBrandParams,
} from "@/features/branding/interfaces";
import type {
  CreateCampaignParams,
  UpdateCampaignParams,
} from "@/features/campaign/interfaces";

const STEPS = [
  {
    id: "details",
    label: "Plan Details",
    description: "Name & description",
    title: "Plan Details",
    stepDescription: "Give your content plan a name and optional description.",
    infoCards: [
      {
        title: "Plan name",
        description:
          "Choose a clear, descriptive name to identify this plan (e.g. 'Q1 Social Campaign').",
        icon: "info" as const,
      },
      {
        title: "Description",
        description:
          "Add an optional description to provide context about this plan's goals and audience.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "demographics",
    label: "Demographics",
    description: "Target audience",
    title: "Demographics",
    stepDescription:
      "Define your target audience. Age group, region, and interests are required.",
    infoCards: [
      {
        title: "Targeting",
        description:
          "Demographics help tailor content to your ideal audience and improve relevance.",
        icon: "info" as const,
      },
      {
        title: "Required fields",
        description:
          "Age group, region, and interests are required. Selected items appear as tags above each field.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "agents",
    label: "Agents",
    description: "Choose AI agents",
    title: "Select Agents",
    stepDescription:
      "Select the AI agents that will generate content for this plan. At least one agent is required.",
    infoCards: [
      {
        title: "Multiple agents",
        description:
          "You can select multiple agents to diversify your content output across different styles and tones.",
        icon: "info" as const,
      },
      {
        title: "Create new agents",
        description:
          "Don't have any agents yet? Click 'Add new' to create agents directly from this step.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "brands",
    label: "Brands",
    description: "Link your brands",
    title: "Select Brands",
    stepDescription:
      "Choose the brands this content plan will represent. At least one brand is required.",
    infoCards: [
      {
        title: "Brand consistency",
        description:
          "Selected brands provide guidelines for logos, tone, and key messaging to keep content on-brand.",
        icon: "info" as const,
      },
      {
        title: "Scrape or create",
        description:
          "Add brands by creating manually or scraping from a website to auto-fill details.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "campaigns",
    label: "Campaigns",
    description: "Assign campaigns",
    title: "Select Campaigns",
    stepDescription:
      "Assign marketing campaigns to this plan. At least one campaign is required.",
    infoCards: [
      {
        title: "Campaign goals",
        description:
          "Each campaign includes goals, CTAs, and FAQs that inform the type of content generated.",
        icon: "info" as const,
      },
      {
        title: "Content themes",
        description:
          "Campaign duration and objectives help align generated content with your marketing timeline.",
        icon: "help" as const,
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Link social accounts",
    title: "Select Integrations",
    stepDescription:
      "Choose which connected social accounts this plan will publish to. At least one integration is required.",
    infoCards: [
      {
        title: "Publish destinations",
        description:
          "Selected integrations are the social accounts where generated content can be published.",
        icon: "info" as const,
      },
      {
        title: "Connect more",
        description:
          "Need to connect Instagram, Facebook, or other platforms? Go to Integrations → Platforms to add them.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    description: "Status & duration",
    title: "Plan Settings",
    stepDescription:
      "Set the status, review mode, and duration for your content plan.",
    infoCards: [
      {
        title: "Draft vs Active",
        description:
          "Save as Draft to review later, or set to Active to start content generation immediately.",
        icon: "info" as const,
      },
      {
        title: "Require Review",
        description:
          "Enable 'Require Review' to approve content before publishing, or use Auto for hands-off generation.",
        icon: "tip" as const,
      },
    ],
  },
  {
    id: "review",
    label: "Review",
    description: "Review & create",
    title: "Review & Finalize",
    stepDescription:
      "Review your configuration and selections. You must select at least one agent, brand, campaign, or integration to create the plan.",
    infoCards: [],
  },
];

const CONTENT_STRATEGIES_KEY = ["content-strategies"];
/** Countdown duration for generate modal (5 min). */
const GENERATE_COUNTDOWN_SECONDS = 5 * 60;

type EntityModalType = "agent" | "brand" | "campaign" | null;
type EntityModalMode = "create" | "edit" | "view" | null;

export const ContentWizard: React.FC = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [viewingStrategyId, setViewingStrategyId] = useState<number | null>(
    null
  );
  const [editingStrategy, setEditingStrategy] =
    useState<ContentStrategyWithDetails | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [planName, setPlanName] = useState("");
  const [planDescription, setPlanDescription] = useState<string | undefined>(
    undefined
  );
  const [agentIds, setAgentIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [campaignIds, setCampaignIds] = useState<number[]>([]);
  const [integrationIds, setIntegrationIds] = useState<number[]>([]);
  const [demographicsConfig, setDemographicsConfig] =
    useState<ContentStrategyConfigDemographics | null>(null);
  const [strategyConfig, setStrategyConfig] = useState<{
    status: ContentStrategyStatus;
    mode: ContentStrategyMode;
    config: ContentStrategyConfig;
  } | null>(null);

  const [entityModal, setEntityModal] = useState<{
    type: EntityModalType;
    mode: EntityModalMode;
    id?: number;
  }>({ type: null, mode: null });

  const [generateModalStrategyId, setGenerateModalStrategyId] = useState<
    number | null
  >(null);
  /** Optional selected agent for generation */
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  /** Countdown from 5 min while generating; simulated progress. */
  const [generateRemainingSeconds, setGenerateRemainingSeconds] = useState(0);
  /** Set on success so user can view and play the video in the modal. */
  const [generatedResult, setGeneratedResult] = useState<{
    videoUrl: string;
    content?: ContentItem;
  } | null>(null);

  const queryClient = useQueryClient();

  // Fetch strategies
  const { data: strategies = [], isLoading: strategiesLoading } = useQuery({
    queryKey: CONTENT_STRATEGIES_KEY,
    queryFn: () => contentService.getAllContentStrategies(),
  });

  // Fetch entities
  const { data: agents = [], isLoading: agentsLoading } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentService.getAllAgents(),
  });

  const { data: brands = [], isLoading: brandsLoading } = useQuery({
    queryKey: ["brands"],
    queryFn: () => brandingService.getAllBrands(),
  });

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => campaignService.getAllCampaigns(),
  });

  const { data: integrations = [], isLoading: integrationsLoading } = useQuery({
    queryKey: ["integrations"],
    queryFn: () => integrationService.getAllIntegrations(),
  });

  const { data: viewingStrategy, isLoading: viewingStrategyLoading } = useQuery(
    {
      queryKey: ["content-strategies", viewingStrategyId],
      queryFn: () => contentService.getContentStrategyById(viewingStrategyId!),
      enabled: viewingStrategyId != null,
    }
  );

  const updateStrategyMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: number;
      status: ContentStrategyStatus;
    }) => contentService.updateContentStrategy({ id, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_STRATEGIES_KEY });
      toast.success("Plan updated");
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to update plan"),
  });

  const updateStrategyFullMutation = useMutation({
    mutationFn: (params: UpdateContentStrategyParams) =>
      contentService.updateContentStrategy(params),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: CONTENT_STRATEGIES_KEY });
      queryClient.invalidateQueries({
        queryKey: ["content-strategies", updated.id],
      });
      toast.success("Content plan updated");
      setEditingStrategy(null);
      setShowWizard(false);
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to update plan"),
  });

  const deleteStrategyMutation = useMutation({
    mutationFn: (id: number) => contentService.deleteContentStrategy({ id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: CONTENT_STRATEGIES_KEY });
      queryClient.removeQueries({ queryKey: ["content-strategies", data.id] });
      toast.success("Content plan deleted");
      setViewingStrategyId(null);
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to delete plan"),
  });

  const createAgentsMutation = useMutation({
    mutationFn: (params: CreateAgentsParams) =>
      agentService.createAgents(params),
    onSuccess: (newAgents) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      setAgentIds((prev) => [...prev, ...newAgents.map((a) => a.id)]);
      toast.success("Agents created");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to create agents"),
  });

  const updateAgentMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: CompanyAgentStatus }) =>
      agentService.activateAgent({ id, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      toast.success("Agent updated");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to update agent"),
  });

  const deleteAgentMutation = useMutation({
    mutationFn: (id: number) => agentService.deleteAgent({ id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      setAgentIds((prev) => prev.filter((i) => i !== data.id));
      toast.success("Agent deleted");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to delete agent"),
  });

  const createBrandMutation = useMutation({
    mutationFn: (params: CreateBrandParams) =>
      brandingService.createBrand(params),
    onSuccess: (newBrand) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      setBrandIds((prev) => [...prev, newBrand.id]);
      toast.success("Brand created");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to create brand"),
  });

  const updateBrandMutation = useMutation({
    mutationFn: (params: UpdateBrandParams) =>
      brandingService.updateBrand(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand updated");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to update brand"),
  });

  const createCampaignMutation = useMutation({
    mutationFn: (params: CreateCampaignParams) =>
      campaignService.createCampaign(params),
    onSuccess: (newCampaign) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setCampaignIds((prev) => [...prev, newCampaign.id]);
      toast.success("Campaign created");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to create campaign"),
  });

  const updateCampaignMutation = useMutation({
    mutationFn: (params: UpdateCampaignParams) =>
      campaignService.updateCampaign(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Campaign updated");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to update campaign"),
  });

  const deleteBrandMutation = useMutation({
    mutationFn: (id: number) => brandingService.deleteBrand({ id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      setBrandIds((prev) => prev.filter((i) => i !== data.id));
      toast.success("Brand deleted");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to delete brand"),
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (id: number) => campaignService.deleteCampaign({ id }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setCampaignIds((prev) => prev.filter((i) => i !== data.id));
      toast.success("Campaign deleted");
      setEntityModal({ type: null, mode: null });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to delete campaign"),
  });

  const createStrategyMutation = useMutation({
    mutationFn: (params: CreateContentStrategyParams) =>
      contentService.createContentStrategy(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTENT_STRATEGIES_KEY });
      toast.success("Content plan created");
      setShowWizard(false);
      setCurrentStep(0);
      setAgentIds([]);
      setBrandIds([]);
      setCampaignIds([]);
      setIntegrationIds([]);
      setDemographicsConfig(null);
      setStrategyConfig(null);
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to create plan"),
  });

  const generateByStrategyMutation = useMutation({
    mutationFn: ({
      contentStrategyId,
      agentId,
    }: {
      contentStrategyId: number;
      agentId?: number;
    }) =>
      contentService.generateByStrategy({
        contentStrategyId,
        type: "VIDEO",
        provider: "GEMINI",
        ...(agentId != null && { agentId }),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["content"] });
      toast.success("Content generated successfully");
      setGeneratedResult({
        videoUrl: data.videoUrl,
        content: data.content,
      });
    },
    onError: (e: unknown) =>
      toast.error(typeof e === "string" ? e : "Failed to generate content"),
  });

  const handleCloseGenerateModal = () => {
    setGenerateModalStrategyId(null);
    setSelectedAgentId(null);
    setGeneratedResult(null);
  };

  // Reset state when generate modal opens
  useEffect(() => {
    if (generateModalStrategyId != null) {
      setGeneratedResult(null);
      setGenerateRemainingSeconds(0);
      setSelectedAgentId(null);
    }
  }, [generateModalStrategyId]);

  // Start countdown when generation starts
  useEffect(() => {
    if (
      generateModalStrategyId == null ||
      !generateByStrategyMutation.isPending
    )
      return;
    setGenerateRemainingSeconds(GENERATE_COUNTDOWN_SECONDS);
  }, [generateModalStrategyId, generateByStrategyMutation.isPending]);

  // Countdown timer (simulated progress) while generating
  useEffect(() => {
    if (
      generateModalStrategyId == null ||
      !generateByStrategyMutation.isPending
    )
      return;
    const id = setInterval(
      () =>
        setGenerateRemainingSeconds((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearInterval(id);
  }, [generateModalStrategyId, generateByStrategyMutation.isPending]);

  const selectedAgent = agents.find((a) => a.id === entityModal.id);
  const selectedBrand = brands.find((b) => b.id === entityModal.id);
  const selectedCampaign = campaigns.find((c) => c.id === entityModal.id);

  const renderEntityModal = () => {
    if (!entityModal.type || !entityModal.mode) return null;

    if (entityModal.type === "agent") {
      if (entityModal.mode === "create") {
        return (
          <CreateAgentsForm
            onSubmit={async (data) => {
              await createAgentsMutation.mutateAsync(data);
            }}
            onCancel={() => setEntityModal({ type: null, mode: null })}
            isLoading={createAgentsMutation.isPending}
          />
        );
      }
      if (entityModal.mode === "edit" && selectedAgent) {
        return (
          <AgentStatusForm
            agent={selectedAgent}
            onSubmit={async (status) => {
              await updateAgentMutation.mutateAsync({
                id: selectedAgent.id,
                status,
              });
            }}
            onCancel={() => setEntityModal({ type: null, mode: null })}
            onDelete={() => deleteAgentMutation.mutate(selectedAgent.id)}
            isLoading={updateAgentMutation.isPending}
          />
        );
      }
    }

    if (entityModal.type === "brand") {
      if (entityModal.mode === "view" && selectedBrand) {
        return (
          <BrandDetail
            brand={selectedBrand}
            onEdit={() =>
              setEntityModal({
                type: "brand",
                mode: "edit",
                id: selectedBrand.id,
              })
            }
            onDelete={(id) => deleteBrandMutation.mutate(id)}
            onBack={() => setEntityModal({ type: null, mode: null })}
          />
        );
      }
      if (entityModal.mode === "create" || entityModal.mode === "edit") {
        return (
          <BrandForm
            brand={entityModal.mode === "edit" ? selectedBrand : undefined}
            onSubmit={async (data) => {
              if (selectedBrand) {
                await updateBrandMutation.mutateAsync(
                  data as UpdateBrandParams
                );
              } else {
                await createBrandMutation.mutateAsync(
                  data as CreateBrandParams
                );
              }
            }}
            onCancel={() => setEntityModal({ type: null, mode: null })}
            isLoading={
              createBrandMutation.isPending || updateBrandMutation.isPending
            }
          />
        );
      }
    }

    if (entityModal.type === "campaign") {
      if (entityModal.mode === "view" && selectedCampaign) {
        return (
          <CampaignDetail
            campaign={selectedCampaign}
            onEdit={() =>
              setEntityModal({
                type: "campaign",
                mode: "edit",
                id: selectedCampaign.id,
              })
            }
            onDelete={(id) => deleteCampaignMutation.mutate(id)}
            onBack={() => setEntityModal({ type: null, mode: null })}
          />
        );
      }
      if (entityModal.mode === "create" || entityModal.mode === "edit") {
        return (
          <CampaignForm
            campaign={
              entityModal.mode === "edit" ? selectedCampaign : undefined
            }
            onSubmit={async (data) => {
              if (selectedCampaign) {
                await updateCampaignMutation.mutateAsync(
                  data as UpdateCampaignParams
                );
              } else {
                await createCampaignMutation.mutateAsync(
                  data as CreateCampaignParams
                );
              }
            }}
            onCancel={() => setEntityModal({ type: null, mode: null })}
            isLoading={
              createCampaignMutation.isPending ||
              updateCampaignMutation.isPending
            }
          />
        );
      }
    }

    return null;
  };

  // Strategy detail view - fetches rich response (brands, campaigns, agents) via getContentStrategyById
  // When editingStrategy is set we show wizard (edit mode) instead of detail
  if (viewingStrategyId != null && !showWizard) {
    return (
      <div className="py-4 sm:py-6 lg:py-8 px-4 sm:px-6">
        <h1 className="text-3xl font-bold mb-2">Plan Details</h1>
        <p className="text-muted-foreground mb-8">
          View configuration and linked entities for this content plan
        </p>
        {viewingStrategyLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : viewingStrategy ? (
          <StrategyDetail
            strategy={viewingStrategy}
            onBack={() => setViewingStrategyId(null)}
            onDelete={(id) => deleteStrategyMutation.mutate(id)}
            isDeleting={deleteStrategyMutation.isPending}
            onGenerate={(id) => setGenerateModalStrategyId(id)}
            isGenerating={generateByStrategyMutation.isPending}
            onEdit={() => {
              const s = viewingStrategy;
              setPlanName(s.name);
              setPlanDescription(s.description);
              setAgentIds(s.agentIds ?? s.agents?.map((a) => a.id) ?? []);
              setBrandIds(s.brandIds ?? s.brands?.map((b) => b.id) ?? []);
              setCampaignIds(
                s.campaignIds ?? s.campaigns?.map((c) => c.id) ?? []
              );
              setIntegrationIds(
                s.integrationIds ?? s.integrations?.map((i) => i.id) ?? []
              );
              setDemographicsConfig(s.config?.demographics ?? null);
              setStrategyConfig({
                status: s.status,
                mode: s.mode,
                config: s.config,
              });
              setCurrentStep(0);
              setEditingStrategy(s);
              setShowWizard(true);
            }}
            onActivate={(id) =>
              updateStrategyMutation.mutate({ id, status: "ACTIVE" })
            }
            onDeactivate={(id) =>
              updateStrategyMutation.mutate({ id, status: "DRAFT" })
            }
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Plan not found</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setViewingStrategyId(null)}
            >
              Back to list
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Default: show strategies list (no wizard and not viewing a strategy)
  if (!showWizard && viewingStrategyId == null) {
    return (
      <>
        <div className="py-4 sm:py-6 lg:py-8 px-4 sm:px-6">
          <h1 className="text-3xl font-bold mb-2">Content Plan Wizard</h1>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <p className="text-muted-foreground">
              Use content plans as your blueprints to automate and scale
              high-quality generation with precision. Activate or deactivate plans
              to control content generation.
            </p>
            <Button
              onClick={() => setShowWizard(true)}
              className="shrink-0 w-fit"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Plan
            </Button>
          </div>
          <StrategyList
            strategies={strategies}
            onActivate={(id) =>
              updateStrategyMutation.mutate({ id, status: "ACTIVE" })
            }
            onView={(strategy) => setViewingStrategyId(strategy.id)}
            onGenerate={(id) => setGenerateModalStrategyId(id)}
            generatingStrategyId={
              generateByStrategyMutation.isPending ? generateModalStrategyId : null
            }
            isLoading={strategiesLoading}
          />
        </div>
        <Dialog
          open={generateModalStrategyId != null}
          onOpenChange={(open) => {
            if (!open && generateByStrategyMutation.isPending) return;
            if (!open) handleCloseGenerateModal();
          }}
          title={
            generatedResult
              ? "Your video is ready"
              : "Generate video content"
          }
          description={
            generateByStrategyMutation.isError
              ? undefined
              : generatedResult
                ? undefined
                : "This will generate a video using your plan's brands, campaigns, and agents. The process can take 4–10 minutes."
          }
          size={generatedResult ? "xl" : "sm"}
          showCloseButton={
            generateByStrategyMutation.isError || generatedResult != null
          }
        >
          <div className="space-y-4 pt-2">
            {generatedResult && (
              <>
                <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                  <video
                    src={generatedResult.videoUrl}
                    controls
                    autoPlay
                    className="w-full h-full object-contain"
                  />
                </div>
                {generatedResult.content?.data?.caption && (
                  <p className="text-sm text-muted-foreground">
                    {generatedResult.content.data.caption}
                  </p>
                )}
                <div className="flex justify-end">
                  <Button onClick={handleCloseGenerateModal}>Close</Button>
                </div>
              </>
            )}
            {generateByStrategyMutation.isError && !generatedResult && (
              <p className="text-sm text-destructive">
                {typeof generateByStrategyMutation.error === "string"
                  ? generateByStrategyMutation.error
                  : generateByStrategyMutation.error instanceof Error
                    ? generateByStrategyMutation.error.message
                    : "Something went wrong. Please try again."}
              </p>
            )}
            {generateByStrategyMutation.isPending && (
              <div className="space-y-2">
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ease-linear ${
                      generateRemainingSeconds <= 0
                        ? "bg-destructive w-full"
                        : "bg-primary"
                    }`}
                    style={{
                      width: `${Math.min(
                        100,
                        ((GENERATE_COUNTDOWN_SECONDS - generateRemainingSeconds) /
                          GENERATE_COUNTDOWN_SECONDS) *
                          100
                      )}%`,
                    }}
                  />
                </div>
                <p
                  className={`text-sm ${
                    generateRemainingSeconds <= 0
                      ? "text-destructive font-medium"
                      : "text-muted-foreground"
                  }`}
                >
                  Remaining:{" "}
                  <strong>
                    {generateRemainingSeconds <= 0
                      ? "0:00 (taking longer than expected)"
                      : `${Math.floor(generateRemainingSeconds / 60)}:${(generateRemainingSeconds % 60).toString().padStart(2, "0")}`}
                  </strong>
                </p>
              </div>
            )}
            {!generateByStrategyMutation.isPending &&
              !generateByStrategyMutation.isError &&
              !generatedResult && (
                <>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Select Agent (Optional)
                      </label>
                      <Select
                        value={selectedAgentId?.toString() ?? "random"}
                        onValueChange={(value) =>
                          setSelectedAgentId(
                            value === "random" ? null : Number(value)
                          )
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Use random agent from strategy">
                            {selectedAgentId ? (
                              <div className="flex items-center gap-2">
                                {agents.find((a) => a.id === selectedAgentId)
                                  ?.images?.[0] && (
                                  <div className="w-6 h-6 rounded overflow-hidden bg-muted shrink-0">
                                    <img
                                      src={
                                        agents.find(
                                          (a) => a.id === selectedAgentId
                                        )?.images?.[0]
                                      }
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                )}
                                <span>
                                  {
                                    agents.find((a) => a.id === selectedAgentId)
                                      ?.name
                                  }
                                </span>
                              </div>
                            ) : (
                              "Use random agent from strategy"
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="random">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded bg-muted shrink-0 flex items-center justify-center">
                                <span className="text-xs">?</span>
                              </div>
                              <span>Use random agent from strategy</span>
                            </div>
                          </SelectItem>
                          {agents
                            .filter((a) => a.status === "ACTIVE")
                            .map((agent) => (
                              <SelectItem
                                key={agent.id}
                                value={agent.id.toString()}
                              >
                                <div className="flex items-center gap-2">
                                  {agent.images?.[0] ? (
                                    <div className="w-6 h-6 rounded overflow-hidden bg-muted shrink-0">
                                      <img
                                        src={agent.images[0]}
                                        alt=""
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 rounded bg-muted shrink-0" />
                                  )}
                                  <span>{agent.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-2">
                        Choose a specific agent or leave blank to randomly
                        select from your strategy&apos;s agents.
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Estimated time: <strong>4–10 minutes</strong>. You can
                    leave this page; we&apos;ll notify you when it&apos;s ready.
                  </p>
                </>
              )}
            {!generatedResult && (
              <div className="flex justify-end gap-2">
                {generateByStrategyMutation.isError ? (
                  <Button onClick={handleCloseGenerateModal}>Close</Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleCloseGenerateModal}
                      disabled={generateByStrategyMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        if (generateModalStrategyId != null) {
                          generateByStrategyMutation.mutate({
                            contentStrategyId: generateModalStrategyId,
                            agentId: selectedAgentId ?? undefined,
                          });
                        }
                      }}
                      disabled={generateByStrategyMutation.isPending}
                    >
                      {generateByStrategyMutation.isPending
                        ? "Generating…"
                        : "Start generation"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </Dialog>
      </>
    );
  }

  // Wizard view: stepper on left, content on right
  const handleCancelWizard = () => {
    setShowWizard(false);
    if (editingStrategy) setEditingStrategy(null);
  };

  return (
    <div className="py-4 sm:py-6 lg:py-8 px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            {editingStrategy ? "Edit Content Plan" : "Create Content Plan"}
          </h1>
          <p className="text-muted-foreground">
            {editingStrategy
              ? "Update configuration and linked entities for this content plan"
              : "Select agents, brands, and campaigns for your content plan"}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleCancelWizard}
          className="shrink-0 w-full sm:w-auto"
        >
          Cancel
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-12 min-h-0 max-h-[calc(100dvh-8rem)] lg:max-h-[calc(100vh-12rem)] overflow-hidden">
        <div className="lg:w-52 shrink-0 pl-4 lg:border-r border-input pr-8 overflow-y-auto overflow-x-auto">
          <WizardStepper
            steps={STEPS.map(({ id, label, description }) => ({
              id,
              label,
              description,
            }))}
            currentStep={currentStep}
          />
        </div>

        <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          {/* Step 0: Plan Details */}
          {currentStep === 0 && (
            <WizardStepContent
              title={STEPS[0].title}
              description={STEPS[0].stepDescription}
              infoCards={STEPS[0].infoCards}
            >
              <PlanDetailsStep
                initialName={planName}
                initialDescription={planDescription}
                onNext={({ name, description }) => {
                  setPlanName(name);
                  setPlanDescription(description);
                  setCurrentStep(1);
                }}
                onBack={() => {
                  setShowWizard(false);
                  if (editingStrategy) setEditingStrategy(null);
                }}
              />
            </WizardStepContent>
          )}

          {/* Step 1: Demographics */}
          {currentStep === 1 && (
            <WizardStepContent
              title={STEPS[1].title}
              description={STEPS[1].stepDescription}
              infoCards={STEPS[1].infoCards}
            >
              <DemographicsConfigStep
                initialDemographics={demographicsConfig ?? undefined}
                onNext={(demographics) => {
                  setDemographicsConfig(demographics);
                  setCurrentStep(2);
                }}
                onBack={() => setCurrentStep(0)}
              />
            </WizardStepContent>
          )}

          {/* Step 2: Agents */}
          {currentStep === 2 && (
            <WizardStepContent
              title={STEPS[2].title}
              description={STEPS[2].stepDescription}
              infoCards={STEPS[2].infoCards}
            >
              <div className="space-y-6">
                <EntitySelector
                  options={agents.map((a) => ({
                    id: a.id,
                    label: a.name,
                    imageUrl: a.images?.[0],
                  }))}
                  selectedIds={agentIds}
                  onChange={setAgentIds}
                  label="Agents *"
                  onAddNew={() =>
                    setEntityModal({ type: "agent", mode: "create" })
                  }
                  isLoading={agentsLoading}
                />
                {agentIds.length === 0 && (
                  <p className="text-sm text-destructive">
                    Select at least one agent to continue
                  </p>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(1)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(3)}
                    disabled={agentIds.length === 0}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </WizardStepContent>
          )}

          {/* Step 3: Brands */}
          {currentStep === 3 && (
            <WizardStepContent
              title={STEPS[3].title}
              description={STEPS[3].stepDescription}
              infoCards={STEPS[3].infoCards}
            >
              <div className="space-y-6">
                <EntitySelector
                  options={brands.map((b) => ({
                    id: b.id,
                    label: b.title,
                    imageUrl: b.images?.[0],
                  }))}
                  selectedIds={brandIds}
                  onChange={setBrandIds}
                  label="Brands *"
                  onAddNew={() =>
                    setEntityModal({ type: "brand", mode: "create" })
                  }
                  onView={(id) =>
                    setEntityModal({ type: "brand", mode: "view", id })
                  }
                  isLoading={brandsLoading}
                />
                {brandIds.length === 0 && (
                  <p className="text-sm text-destructive">
                    Select at least one brand to continue
                  </p>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(4)}
                    disabled={brandIds.length === 0}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </WizardStepContent>
          )}

          {/* Step 4: Campaigns */}
          {currentStep === 4 && (
            <WizardStepContent
              title={STEPS[4].title}
              description={STEPS[4].stepDescription}
              infoCards={STEPS[4].infoCards}
            >
              <div className="space-y-6">
                <EntitySelector
                  options={campaigns.map((c) => ({
                    id: c.id,
                    label: c.title,
                    imageUrl: c.images?.[0],
                  }))}
                  selectedIds={campaignIds}
                  onChange={setCampaignIds}
                  label="Campaigns *"
                  onAddNew={() =>
                    setEntityModal({ type: "campaign", mode: "create" })
                  }
                  onView={(id) =>
                    setEntityModal({ type: "campaign", mode: "view", id })
                  }
                  isLoading={campaignsLoading}
                />
                {campaignIds.length === 0 && (
                  <p className="text-sm text-destructive">
                    Select at least one campaign to continue
                  </p>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(3)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(5)}
                    disabled={campaignIds.length === 0}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </WizardStepContent>
          )}

          {/* Step 5: Integrations */}
          {currentStep === 5 && (
            <WizardStepContent
              title={STEPS[5].title}
              description={STEPS[5].stepDescription}
              infoCards={STEPS[5].infoCards}
            >
              <div className="space-y-6">
                <EntitySelector
                  options={integrations.map((int) => {
                    const a = int.accountInfo;
                    const label = a?.username
                      ? `${int.platform} @${a.username}`
                      : a?.pageName
                      ? `${int.platform} ${a.pageName}`
                      : a?.displayName
                      ? `${int.platform} ${a.displayName}`
                      : a?.channelTitle
                      ? `${int.platform} ${a.channelTitle}`
                      : a?.name
                      ? `${int.platform} ${a.name}`
                      : int.platform;
                    return {
                      id: int.id,
                      label,
                    };
                  })}
                  selectedIds={integrationIds}
                  onChange={setIntegrationIds}
                  label="Integrations *"
                  onAddNew={() =>
                    window.open("/home/integrations/platforms", "_blank")
                  }
                  isLoading={integrationsLoading}
                />
                {integrationIds.length === 0 && (
                  <p className="text-sm text-destructive">
                    Select at least one integration to continue
                  </p>
                )}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setCurrentStep(4)}>
                    Back
                  </Button>
                  <Button
                    onClick={() => setCurrentStep(6)}
                    disabled={integrationIds.length === 0}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </WizardStepContent>
          )}

          {/* Step 6: Plan Settings */}
          {currentStep === 6 && demographicsConfig && (
            <WizardStepContent
              title={STEPS[6].title}
              description={STEPS[6].stepDescription}
              infoCards={STEPS[6].infoCards}
            >
              <StrategySettingsStep
                initialStatus={strategyConfig?.status}
                initialMode={strategyConfig?.mode}
                initialPeriod={strategyConfig?.config?.duration?.period}
                initialPeriodCount={
                  strategyConfig?.config?.duration?.periodCount
                }
                onNext={({ status, mode, duration }) => {
                  setStrategyConfig({
                    status,
                    mode,
                    config: {
                      demographics: demographicsConfig,
                      duration,
                    },
                  });
                  setCurrentStep(7);
                }}
                onBack={() => setCurrentStep(5)}
              />
            </WizardStepContent>
          )}

          {/* Step 7: Review */}
          {currentStep === 7 && strategyConfig && (
            <WizardStepContent
              title={STEPS[7].title}
              description={STEPS[7].stepDescription}
              infoCards={STEPS[7].infoCards}
            >
              <StrategyReviewStep
                planName={planName}
                planDescription={planDescription}
                config={strategyConfig}
                agentIds={agentIds}
                brandIds={brandIds}
                campaignIds={campaignIds}
                integrationIds={integrationIds}
                resolvedEntities={{
                  agents: agentIds
                    .map((id) => agents.find((a) => a.id === id))
                    .filter(Boolean)
                    .map((a) => ({
                      id: a!.id,
                      name: a!.name,
                      imageUrl: a!.images?.[0],
                    })),
                  brands: brandIds
                    .map((id) => brands.find((b) => b.id === id))
                    .filter(Boolean)
                    .map((b) => ({
                      id: b!.id,
                      title: b!.title,
                      imageUrl: b!.images?.[0],
                    })),
                  campaigns: campaignIds
                    .map((id) => campaigns.find((c) => c.id === id))
                    .filter(Boolean)
                    .map((c) => ({
                      id: c!.id,
                      title: c!.title,
                      imageUrl: c!.images?.[0],
                    })),
                  integrations: integrationIds
                    .map((id) => integrations.find((i) => i.id === id))
                    .filter(Boolean)
                    .map((i) => {
                      const a = i!.accountInfo;
                      const label = a?.username
                        ? `@${a.username}`
                        : a?.pageName ??
                          a?.displayName ??
                          a?.channelTitle ??
                          a?.name ??
                          i!.platform;
                      return {
                        id: i!.id,
                        label: `${i!.platform} ${label}`,
                      };
                    }),
                }}
                onBack={() => setCurrentStep(6)}
                onCreate={() => {
                  const hasSelection =
                    agentIds.length > 0 ||
                    brandIds.length > 0 ||
                    campaignIds.length > 0 ||
                    integrationIds.length > 0;
                  if (!hasSelection) return;
                  if (editingStrategy) {
                    updateStrategyFullMutation.mutate({
                      id: editingStrategy.id,
                      name: planName,
                      description: planDescription,
                      status: strategyConfig.status,
                      mode: strategyConfig.mode,
                      config: strategyConfig.config,
                      agentIds: agentIds.length > 0 ? agentIds : undefined,
                      brandIds: brandIds.length > 0 ? brandIds : undefined,
                      campaignIds:
                        campaignIds.length > 0 ? campaignIds : undefined,
                      integrationIds:
                        integrationIds.length > 0 ? integrationIds : undefined,
                    });
                  } else {
                    createStrategyMutation.mutate({
                      name: planName,
                      description: planDescription,
                      status: strategyConfig.status,
                      mode: strategyConfig.mode,
                      config: strategyConfig.config,
                      agentIds: agentIds.length > 0 ? agentIds : undefined,
                      brandIds: brandIds.length > 0 ? brandIds : undefined,
                      campaignIds:
                        campaignIds.length > 0 ? campaignIds : undefined,
                      integrationIds:
                        integrationIds.length > 0 ? integrationIds : undefined,
                    });
                  }
                }}
                isLoading={
                  editingStrategy
                    ? updateStrategyFullMutation.isPending
                    : createStrategyMutation.isPending
                }
                submitLabel={editingStrategy ? "Update plan" : undefined}
                validationError={
                  agentIds.length === 0 &&
                  brandIds.length === 0 &&
                  campaignIds.length === 0 &&
                  integrationIds.length === 0
                    ? "Select at least one agent, brand, campaign, or integration to create the plan."
                    : undefined
                }
              />
            </WizardStepContent>
          )}
        </div>
      </div>

      <Dialog
        open={entityModal.type !== null && entityModal.mode !== null}
        onOpenChange={(open) =>
          !open && setEntityModal({ type: null, mode: null })
        }
        title={
          entityModal.type === "agent"
            ? entityModal.mode === "create"
              ? "Create Agents"
              : "Edit Agent"
            : entityModal.type === "brand"
            ? entityModal.mode === "view"
              ? ""
              : entityModal.mode === "create"
              ? "Create Brand"
              : "Edit Brand"
            : entityModal.type === "campaign"
            ? entityModal.mode === "view"
              ? ""
              : entityModal.mode === "create"
              ? "Create Campaign"
              : "Edit Campaign"
            : ""
        }
        size={entityModal.mode === "view" ? "full" : "xl"}
      >
        {renderEntityModal()}
      </Dialog>

      <Dialog
        open={generateModalStrategyId != null}
        onOpenChange={(open) => {
          if (!open && generateByStrategyMutation.isPending) return;
          if (!open) handleCloseGenerateModal();
        }}
        title={
          generatedResult
            ? "Your video is ready"
            : "Generate video content"
        }
        description={
          generateByStrategyMutation.isError
            ? undefined
            : generatedResult
              ? undefined
              : "This will generate a video using your plan's brands, campaigns, and agents. The process can take 4–10 minutes."
        }
        size={generatedResult ? "xl" : "sm"}
        showCloseButton={
          generateByStrategyMutation.isError || generatedResult != null
        }
      >
        <div className="space-y-4 pt-2">
          {generatedResult && (
            <>
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <video
                  src={generatedResult.videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                />
              </div>
              {generatedResult.content?.data?.caption && (
                <p className="text-sm text-muted-foreground">
                  {generatedResult.content.data.caption}
                </p>
              )}
              <div className="flex justify-end">
                <Button onClick={handleCloseGenerateModal}>Close</Button>
              </div>
            </>
          )}
          {generateByStrategyMutation.isError && !generatedResult && (
            <p className="text-sm text-destructive">
              {typeof generateByStrategyMutation.error === "string"
                ? generateByStrategyMutation.error
                : generateByStrategyMutation.error instanceof Error
                  ? generateByStrategyMutation.error.message
                  : "Something went wrong. Please try again."}
            </p>
          )}
          {generateByStrategyMutation.isPending && (
            <div className="space-y-2">
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ease-linear ${
                    generateRemainingSeconds <= 0
                      ? "bg-destructive w-full"
                      : "bg-primary"
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      ((GENERATE_COUNTDOWN_SECONDS - generateRemainingSeconds) /
                        GENERATE_COUNTDOWN_SECONDS) *
                        100
                    )}%`,
                  }}
                />
              </div>
              <p
                className={`text-sm ${
                  generateRemainingSeconds <= 0
                    ? "text-destructive font-medium"
                    : "text-muted-foreground"
                }`}
              >
                Remaining:{" "}
                <strong>
                  {generateRemainingSeconds <= 0
                    ? "0:00 (taking longer than expected)"
                    : `${Math.floor(generateRemainingSeconds / 60)}:${(generateRemainingSeconds % 60).toString().padStart(2, "0")}`}
                </strong>
              </p>
            </div>
          )}
          {!generateByStrategyMutation.isPending &&
            !generateByStrategyMutation.isError &&
            !generatedResult && (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Select Agent (Optional)
                    </label>
                    <Select
                      value={selectedAgentId?.toString() ?? "random"}
                      onValueChange={(value) =>
                        setSelectedAgentId(
                          value === "random" ? null : Number(value)
                        )
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Use random agent from strategy">
                          {selectedAgentId ? (
                            <div className="flex items-center gap-2">
                              {agents.find((a) => a.id === selectedAgentId)
                                ?.images?.[0] && (
                                <div className="w-6 h-6 rounded overflow-hidden bg-muted shrink-0">
                                  <img
                                    src={
                                      agents.find(
                                        (a) => a.id === selectedAgentId
                                      )?.images?.[0]
                                    }
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <span>
                                {
                                  agents.find((a) => a.id === selectedAgentId)
                                    ?.name
                                }
                              </span>
                            </div>
                          ) : (
                            "Use random agent from strategy"
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="random">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded bg-muted shrink-0 flex items-center justify-center">
                              <span className="text-xs">?</span>
                            </div>
                            <span>Use random agent from strategy</span>
                          </div>
                        </SelectItem>
                        {agents
                          .filter((a) => a.status === "ACTIVE")
                          .map((agent) => (
                            <SelectItem
                              key={agent.id}
                              value={agent.id.toString()}
                            >
                              <div className="flex items-center gap-2">
                                {agent.images?.[0] ? (
                                  <div className="w-6 h-6 rounded overflow-hidden bg-muted shrink-0">
                                    <img
                                      src={agent.images[0]}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-6 h-6 rounded bg-muted shrink-0" />
                                )}
                                <span>{agent.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-2">
                      Choose a specific agent or leave blank to randomly
                      select from your strategy&apos;s agents.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Estimated time: <strong>4–10 minutes</strong>. You can leave
                  this page; we&apos;ll notify you when it&apos;s ready.
                </p>
              </>
            )}
          {!generatedResult && (
            <div className="flex justify-end gap-2">
              {generateByStrategyMutation.isError ? (
                <Button onClick={handleCloseGenerateModal}>Close</Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCloseGenerateModal}
                    disabled={generateByStrategyMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (generateModalStrategyId != null) {
                        generateByStrategyMutation.mutate({
                          contentStrategyId: generateModalStrategyId,
                          agentId: selectedAgentId ?? undefined,
                        });
                      }
                    }}
                    disabled={generateByStrategyMutation.isPending}
                  >
                    {generateByStrategyMutation.isPending
                      ? "Generating…"
                      : "Start generation"}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

function AgentStatusForm({
  agent,
  onSubmit,
  onCancel,
  onDelete,
  isLoading,
}: {
  agent: Agent;
  onSubmit: (status: AgentStatus) => Promise<void>;
  onCancel: () => void;
  onDelete: () => void;
  isLoading: boolean;
}) {
  const [status, setStatus] = React.useState<AgentStatus>(agent.status);

  return (
    <div className="space-y-6">
      {agent.images?.[0] && (
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0">
          <img
            src={agent.images[0]}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <p className="text-sm text-muted-foreground">{agent.name}</p>
      <select
        value={status}
        onChange={(e) => setStatus(e.target.value as AgentStatus)}
        className="w-full px-3 py-2 border border-input rounded-md bg-background"
      >
        <option value="ACTIVE">Active</option>
        <option value="REVIEW">Review</option>
        <option value="ARCHIVED">Archived</option>
      </select>
      <div className="flex justify-between">
        <Button
          variant="destructive"
          onClick={() => {
            if (window.confirm(`Delete "${agent.name}"?`)) {
              onDelete();
            }
          }}
          disabled={isLoading}
        >
          Delete Agent
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(status)} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
